import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The grip that starts a drag, and the look of a row while one is under way.
 *
 * Three screens drew their own: two icon sizes of one glyph, a faded row with a
 * shadow copied four times, and a landing mark written two ways. The design
 * draws one six-dot grip and one dashed outline wherever something can be
 * carried, which is what lets an attorney who has dragged a figure know a claim
 * can be dragged too.
 *
 * The handle is the only thing that starts a drag. A row full of controls would
 * otherwise start one on every press meant for a button inside it.
 */
const SIZE = {
  /** A dense row: a navigator entry, a figure card. */
  sm: "size-4",
  /** A claim, a position waiting to be placed. */
  md: "size-5",
  /** The heading of a whole group of rows. */
  lg: "size-[22px]",
} as const;

export type DragHandleSize = keyof typeof SIZE;

type DragHandleProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "type" | "children" | "aria-label"
> & {
  /** What pressing it moves, as a reader hears it: "Reorder claim 2". */
  label: string;
  size?: DragHandleSize;
  /** Where the drag machinery attaches its listeners. */
  handleRef?: (element: Element | null) => void;
};

function Grip({ size }: { size: DragHandleSize }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={SIZE[size]}
      fill="currentColor"
    >
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </svg>
  );
}

export function DragHandle({
  label,
  size = "md",
  handleRef,
  className,
  ...rest
}: DragHandleProps) {
  return (
    <button
      type="button"
      ref={handleRef}
      aria-label={label}
      className={cn(
        "inline-flex flex-none cursor-grab touch-none items-center justify-center rounded-sm text-subtle-foreground hover:bg-muted hover:text-muted-foreground active:cursor-grabbing",
        className,
      )}
      {...rest}
    >
      <Grip size={size} />
    </button>
  );
}

/** The grip alone, as decoration, for words that tell a reader what to drag: "drag ⠿ to reorder". */
export function DragMark() {
  return (
    <span className="inline-flex align-middle text-subtle-foreground">
      <Grip size="sm" />
    </span>
  );
}

/**
 * A row's look during a drag: faded while it is the one being carried, and
 * outlined while a release would land on it. The row being carried is never
 * also the landing, since it is sitting where it would land.
 */
export function dragChrome({
  dragging = false,
  landing = false,
}: {
  dragging?: boolean;
  landing?: boolean;
}): string {
  if (dragging) return "opacity-40";
  if (landing) {
    return "outline-2 outline-offset-2 outline-dashed outline-ring";
  }
  return "";
}

/**
 * What follows the pointer while an item is carried onto a target. The item
 * itself stays in its place, faded; a full-width copy would run past the card
 * it is carried over, so the copy is its words, capped and cut short.
 */
export function DragPreview({ children }: { children: ReactNode }) {
  return (
    <div
      data-drag-preview
      className="inline-flex max-w-[360px] items-center gap-2 rounded-inset border border-border bg-card py-2 pr-3 pl-1.5 text-[13px] text-foreground shadow-popover"
    >
      <span className="flex-none text-subtle-foreground">
        <Grip size="md" />
      </span>
      <span className="truncate">{children}</span>
    </div>
  );
}
